const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const STATE_KEY_ALIASES = [
    ['displayLabel', 'label'],
    ['displayAsText', 'asText'],
    ['capitalize', 'textCase'],
    ['prefix', 'textPrefix'],
    ['suffix', 'textSuffix'],
    ['listAsTextSeparator', 'asTextSeparator'],
    ['dateAsTextFormat', 'dateFormat'],
    ['showTime', 'dateIncludeTime'],
    ['showAsDays', 'dateRelativeUnit'],
    ['url', 'linkUrl'],
    ['urlStorage', 'linkUrlStorage'],
    ['disableNewLinkIcon', 'linkDisableTabIcon'],
    ['value_format', 'numberUnit'],
    ['useShorthand', 'numberFormat'],
    ['numberDisplayFormat', 'numberFormat'],
    ['precision', 'numberPrecision'],
    ['locale', 'numberLocale'],
    ['nullDisplay', 'numberNullDisplay'],
    ['roundingMode', 'numberRoundingMode'],
    ['goalDirection', 'goalCondition'],
    ['displaySize', 'size'],
    ['value_font_size', 'size'],
    ['justifyContent', 'contentAlignment'],
    ['_styles', 'styles'],
    ['tooltipOptions', '_tooltip'],
    ['useFallbackTooltip', '_tooltip'],
    ['_conditionalColorRules', 'conditionalColorRules'],
    ['conditional_color', 'conditionalColorRules'],
    ['expandAs', 'expansionMode'],
    ['filterOnLocator', 'filterLocator'],
    ['sortOnLocator', 'sortLocator'],
    ['distinctFilterInputType', 'filterDistinctInputType'],
    ['distinctValueKey', 'filterDistinctValueKey'],
    ['tableState', '_tableColumns'],
    ['props', 'widgetProps'],
];
const LEGACY_TO_CANONICAL = new Map(STATE_KEY_ALIASES);
const setIfAbsent = (target, key, value) => {
    if (!(key in target)) {
        target[key] = value;
    }
};
const normalizeShowAsDays = (value) => {
    if (value === true)
        return 'days';
    if (value === false)
        return undefined;
    return value;
};
const normalizeUseShorthand = (value) => {
    if (value === true)
        return 'abbreviated';
    if (value === false)
        return 'standard';
    return value;
};
const normalizeNumberFormatValue = (value) => {
    if (value === 'number')
        return 'standard';
    return value;
};
const normalizeFormatWrapper = (format, target) => {
    if (!isPlainObject(format)) {
        if (typeof format === 'string') {
            setIfAbsent(target, 'numberUnit', format);
        }
        return;
    }
    if ('style' in format)
        setIfAbsent(target, 'numberUnit', format.style);
    if ('useShorthand' in format) {
        setIfAbsent(target, 'numberFormat', normalizeUseShorthand(format.useShorthand));
    }
    if ('precision' in format)
        setIfAbsent(target, 'numberPrecision', format.precision);
    if ('locale' in format)
        setIfAbsent(target, 'numberLocale', format.locale);
    if ('nullDisplay' in format)
        setIfAbsent(target, 'numberNullDisplay', format.nullDisplay);
    if ('roundingMode' in format)
        setIfAbsent(target, 'numberRoundingMode', format.roundingMode);
};
const normalizeDateOptions = (dateOptions, target) => {
    if (!isPlainObject(dateOptions))
        return;
    if ('showTime' in dateOptions) {
        setIfAbsent(target, 'dateIncludeTime', dateOptions.showTime);
    }
};
export const normalizeStateObject = (input, context = 'state') => {
    const result = {};
    const legacyEntries = [];
    for (const [key, value] of Object.entries(input)) {
        const isTooltipValueAlias = context === 'tooltip' && (key === 'value' || key === 'displayValue');
        if (LEGACY_TO_CANONICAL.has(key) || isTooltipValueAlias) {
            legacyEntries.push([key, value]);
            continue;
        }
        if (key === 'enableFilter' || key === 'format' || key === 'dateOptions') {
            legacyEntries.push([key, value]);
            continue;
        }
        result[key] = value;
    }
    for (const [legacyKey, legacyValue] of legacyEntries) {
        if (legacyKey === 'enableFilter') {
            if (legacyValue === false) {
                setIfAbsent(result, 'filterDisabled', true);
            }
            continue;
        }
        if (legacyKey === 'format') {
            normalizeFormatWrapper(legacyValue, result);
            continue;
        }
        if (legacyKey === 'dateOptions') {
            normalizeDateOptions(legacyValue, result);
            continue;
        }
        if (legacyKey === 'useFallbackTooltip') {
            const existing = isPlainObject(result._tooltip)
                ? result._tooltip
                : undefined;
            const next = {
                ...(existing ?? {}),
                useFallback: legacyValue,
            };
            result._tooltip = next;
            continue;
        }
        const canonical = context === 'tooltip' &&
            (legacyKey === 'value' || legacyKey === 'displayValue')
            ? 'asValue'
            : LEGACY_TO_CANONICAL.get(legacyKey);
        if (!canonical)
            continue;
        let nextValue = legacyValue;
        if (legacyKey === 'showAsDays') {
            nextValue = normalizeShowAsDays(legacyValue);
            if (nextValue === undefined)
                continue;
        }
        if (legacyKey === 'useShorthand') {
            nextValue = normalizeUseShorthand(legacyValue);
        }
        if (legacyKey === 'numberDisplayFormat') {
            nextValue = normalizeNumberFormatValue(legacyValue);
        }
        setIfAbsent(result, canonical, nextValue);
    }
    if (result.numberFormat === 'number') {
        result.numberFormat = 'standard';
    }
    for (const [key, value] of Object.entries(result)) {
        if (key === '_tooltip') {
            if (isPlainObject(value)) {
                result[key] = normalizeStateObject(value, 'tooltip');
            }
            continue;
        }
        if (key === '_tableColumns') {
            result[key] = normalizeTableColumns(value);
            continue;
        }
    }
    return result;
};
const normalizeTableColumns = (tableColumns) => {
    if (!isPlainObject(tableColumns))
        return tableColumns;
    const next = { ...tableColumns };
    if (Array.isArray(next.columns)) {
        next.columns = next.columns.map(column => {
            if (!isPlainObject(column))
                return column;
            const col = { ...column };
            if (isPlainObject(col.state)) {
                col.state = normalizeStateObject(col.state);
            }
            return col;
        });
    }
    return next;
};
export const normalizePersistedStateTree = (input) => {
    if (Array.isArray(input)) {
        return input.map(normalizePersistedStateTree);
    }
    if (!isPlainObject(input)) {
        return input;
    }
    const result = {};
    for (const [key, value] of Object.entries(input)) {
        if (key === 'state' && isPlainObject(value)) {
            result[key] = normalizeStateObject(normalizePersistedStateTree(value));
            continue;
        }
        if (key === 'tableState' && !('_tableColumns' in input)) {
            result._tableColumns = normalizeTableColumns(normalizePersistedStateTree(value));
            continue;
        }
        if (key === 'viewState' ||
            key === 'tableConfig' ||
            key === 'widgetStates') {
            result[key] = normalizePersistedStateTree(value);
            continue;
        }
        result[key] = normalizePersistedStateTree(value);
    }
    return result;
};
export const normalizeSavedViewPayload = (savedView) => {
    return normalizePersistedStateTree(savedView);
};
export const normalizeUrlStorageValue = (value) => {
    return normalizePersistedStateTree(value);
};
export const normalizeExportInput = (input) => {
    return normalizePersistedStateTree(input);
};
export const StateAliases = {
    normalizeStateObject,
    normalizePersistedStateTree,
    normalizeSavedViewPayload,
    normalizeUrlStorageValue,
    normalizeExportInput,
    LEGACY_TO_CANONICAL,
};
//# sourceMappingURL=StateAliases.js.map